import React from "react";
import {
  AccordionWrapper,
  AccordionHeader,
  ChevronCell,
  ChevronCircle,
} from "./Accordion.styled";

/**
 * Generic SVG chevron icon (down arrow).
 */
const ChevronSvg = ({ size = 13, color = "currentColor" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block", flexShrink: 0 }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/**
 * Generic expandable row with a chevron toggle.
 *
 * The `header` prop is rendered as the clickable row content. The chevron
 * is appended at the end. `children` render below when expanded.
 *
 * Clicks on elements with `noExpandClass` (default "kpi-no-expand") won't
 * trigger toggle, allowing checkboxes/buttons inside the header.
 *
 * @param {boolean}       expanded        — controlled expand state
 * @param {function}      onToggle        — called on header click
 * @param {ReactNode}     header          — row content (cells etc.)
 * @param {ReactNode}     children        — expanded panel content
 * @param {string}        [noExpandClass] — class name to prevent toggle
 * @param {number}        [chevronSize]   — chevron icon size
 * @param {string}        [$bg]           — row background color
 * @param {string}        [$shadow]       — row box-shadow
 * @param {string}        [$borderBottom] — row bottom border
 * @param {string}        [$mb]           — row margin-bottom
 */
const Accordion = ({
  expanded = false,
  onToggle,
  header,
  children,
  noExpandClass = "kpi-no-expand",
  chevronSize = 13,
  ...wrapperProps
}) => {
  const handleClick = (e) => {
    if (e.target.closest(`.${noExpandClass}`)) return;
    onToggle?.();
  };

  return (
    <AccordionWrapper {...wrapperProps}>
      <AccordionHeader onClick={handleClick}>
        {header}
        <ChevronCell>
          <ChevronCircle $expanded={expanded}>
            <ChevronSvg
              size={chevronSize}
              color={expanded ? "var(--accent)" : "var(--ink-4)"}
            />
          </ChevronCircle>
        </ChevronCell>
      </AccordionHeader>
      {expanded && children}
    </AccordionWrapper>
  );
};

export default Accordion;
